import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SportsCricketIcon from '@mui/icons-material/SportsCricket';
import ScheduleIcon from '@mui/icons-material/Schedule';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import LayersIcon from '@mui/icons-material/Layers';
import { FloatingSideKick } from '../../../../components/ReelMaker/FloatingSideKick';
import { LayersPanel } from '../../../../components/ReelMaker/LayersPanel';
import {
    COMPOSER_MAX_WIDGETS,
    ScreenWidget,
    ScreenWidgetType,
    CricketWidgetPayload,
    StockQuote,
    WIDGET_LABELS,
    WeatherNow,
    createWidget,
    screenDesignSize,
} from '../../../../components/ScreenComposer/composer.types';
import { ClockWidgetView, CricketScoreView, NewsTickerView, OnDemandPlayerView, ReelPlayerWidgetView, StockTickerView, WeatherWidgetView } from '../../../../components/ScreenComposer/WidgetViews';
import { screensApi } from '../Screens.api';
import { Screen } from '../Screens.types';
import { ComposerWidgetFrame } from './ComposerWidgetFrame';
import { WidgetSettingsDialog } from './WidgetSettingsDialog';
import { newsItemsFrom } from './composerPreview';

export interface ScreenComposerProps {
    screen: Screen;
    onSaved: (screen: Screen) => void;
    onMessage: (message: string, severity?: 'success' | 'error') => void;
    onDirtyChange?: (dirty: boolean) => void;
}

const STAGE_PADDING = 24;

const TYPE_ICON: Record<ScreenWidgetType, React.ReactNode> = {
    'reel-player': <SmartDisplayIcon fontSize="inherit" />,
    weather: <WbSunnyIcon fontSize="inherit" />,
    'news-ticker': <NewspaperIcon fontSize="inherit" />,
    'stock-ticker': <ShowChartIcon fontSize="inherit" />,
    'cricket-score': <SportsCricketIcon fontSize="inherit" />,
    clock: <ScheduleIcon fontSize="inherit" />,
    'on-demand-player': <OndemandVideoIcon fontSize="inherit" />,
};

const pct = (n: number) => `${Math.round(n * 100)}%`;

/**
 * Composer tab of a screen: lay out a Reel Player (the playlist), weather, a news ticker and clocks
 * on the screen's own aspect ratio. Saved on the screen; the device renders the same layout.
 */
export const ScreenComposer: React.FC<ScreenComposerProps> = ({ screen, onSaved, onMessage, onDirtyChange }) => {
    const theme = useTheme();
    const viewOnly = useMediaQuery(theme.breakpoints.down('md')) || useMediaQuery('(pointer: coarse)');
    const stageRef = useRef<HTMLDivElement | null>(null);
    const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
    const [widgets, setWidgets] = useState<ScreenWidget[]>(() => screen.composer?.widgets ?? []);
    const [revision, setRevision] = useState(screen.composer?.revision ?? 0);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [layersOpen, setLayersOpen] = useState(false);
    const [settingsId, setSettingsId] = useState<string | null>(null);
    const [weather, setWeather] = useState<Record<string, WeatherNow | null>>({});
    const [news, setNews] = useState<Record<string, Array<Record<string, string>>>>({});
    const [stocks, setStocks] = useState<Record<string, StockQuote[]>>({});
    const [cricket, setCricket] = useState<Record<string, CricketWidgetPayload>>({});

    const design = useMemo(() => screenDesignSize(screen.deviceStatus?.resolution, screen.settings?.orientation), [screen.deviceStatus?.resolution, screen.settings?.orientation]);

    useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);

    // A saved change from elsewhere (another admin) replaces the layout only when nothing is pending here.
    useEffect(() => {
        const incoming = screen.composer?.revision ?? 0;
        if (!dirty && incoming !== revision) {
            setWidgets(screen.composer?.widgets ?? []);
            setRevision(incoming);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screen.composer?.revision]);

    useLayoutEffect(() => {
        const el = stageRef.current;
        if (!el) return undefined;
        const update = () => setStageSize({ width: el.clientWidth, height: el.clientHeight });
        update();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update);
            return () => window.removeEventListener('resize', update);
        }
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const scale = useMemo(() => {
        if (stageSize.width <= 0 || stageSize.height <= 0) return 0.5;
        return Math.min(1, (stageSize.width - STAGE_PADDING * 2) / design.width, (stageSize.height - STAGE_PADDING * 2) / design.height);
    }, [stageSize, design]);

    // ---- live previews (weather per location, news per feed) ----
    const weatherKeys = widgets
        .filter((w) => w.type === 'weather')
        .map((w) => (w.type === 'weather' ? `${w.id}|${w.config.locationOverride}|${w.config.labelOverride}` : ''))
        .join('\n');
    useEffect(() => {
        let cancelled = false;
        const targets = widgets.filter((w) => w.type === 'weather');
        void Promise.all(
            targets.map(async (w) => {
                if (w.type !== 'weather') return;
                try {
                    const res = await screensApi.weatherPreview(screen._id, w.config.locationOverride, w.config.labelOverride);
                    if (cancelled) return;
                    setWeather((m) => ({
                        ...m,
                        [w.id]: res.available && typeof res.tempC === 'number' ? { location: res.location || '', tempC: res.tempC, icon: res.icon || '', label: res.label || '' } : null,
                    }));
                } catch {
                    if (!cancelled) setWeather((m) => ({ ...m, [w.id]: null }));
                }
            }),
        );
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weatherKeys, screen._id, screen.location]);

    const stockKey = widgets
        .filter((w) => w.type === 'stock-ticker')
        .map((w) => (w.type === 'stock-ticker' ? `${w.id}|${w.config.symbols.join(',')}|${w.config.serverRefreshSec}|${w.config.tickerRefreshSec}` : ''))
        .join('\n');
    useEffect(() => {
        const targets = widgets.filter((w) => w.type === 'stock-ticker');
        if (!targets.length) return undefined;
        let cancelled = false;
        const timers: ReturnType<typeof setInterval>[] = [];
        const load = (id: string, symbols: string[], refreshSec: number) => {
            screensApi
                .stockQuotes(symbols, refreshSec)
                .then((res) => {
                    if (!cancelled) setStocks((m) => ({ ...m, [id]: res.quotes }));
                })
                .catch(() => undefined);
        };
        for (const w of targets) {
            if (w.type !== 'stock-ticker') continue;
            load(w.id, w.config.symbols, w.config.serverRefreshSec);
            timers.push(setInterval(() => load(w.id, w.config.symbols, w.config.serverRefreshSec), Math.max(15, w.config.tickerRefreshSec) * 1000));
        }
        return () => {
            cancelled = true;
            timers.forEach(clearInterval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stockKey]);

    const cricketKey = widgets
        .filter((w) => w.type === 'cricket-score')
        .map((w) => (w.type === 'cricket-score' ? `${w.id}|${w.config.teams.join(',')}|${w.config.matchTypes.join(',')}|${w.config.displayRefreshSec}` : ''))
        .join('\n');
    useEffect(() => {
        const targets = widgets.filter((w) => w.type === 'cricket-score');
        if (!targets.length) return undefined;
        let cancelled = false;
        const timers: ReturnType<typeof setInterval>[] = [];
        const load = (id: string, teams: string[], matchTypes: string[]) => {
            screensApi.cricketScore(teams, matchTypes).then((res) => { if (!cancelled) setCricket((m) => ({ ...m, [id]: res })); }).catch(() => undefined);
        };
        for (const w of targets) {
            if (w.type !== 'cricket-score') continue;
            load(w.id, w.config.teams, w.config.matchTypes);
            timers.push(setInterval(() => load(w.id, w.config.teams, w.config.matchTypes), Math.max(5, w.config.displayRefreshSec) * 1000));
        }
        return () => { cancelled = true; timers.forEach(clearInterval); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cricketKey]);

    const feedUrls = [...new Set(widgets.flatMap((w) => (w.type === 'news-ticker' && w.config.feedUrl ? [w.config.feedUrl] : [])))].join('\n');
    useEffect(() => {
        let cancelled = false;
        for (const url of feedUrls.split('\n').filter(Boolean)) {
            if (news[url]) continue;
            screensApi
                .newsPreview(url)
                .then((feed) => {
                    if (!cancelled) setNews((m) => ({ ...m, [url]: feed.items }));
                })
                .catch(() => {
                    if (!cancelled) setNews((m) => ({ ...m, [url]: [] }));
                });
        }
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feedUrls]);

    // ---- edits ----
    const replaceWidget = useCallback((next: ScreenWidget, commit = true) => {
        setWidgets((list) => list.map((w) => (w.id === next.id ? next : w)));
        if (commit) setDirty(true);
    }, []);

    const handleAdd = (type: ScreenWidgetType) => {
        if (widgets.length >= COMPOSER_MAX_WIDGETS) {
            onMessage(`A screen can have at most ${COMPOSER_MAX_WIDGETS} widgets`, 'error');
            return;
        }
        if (type === 'reel-player' && widgets.some((w) => w.type === 'reel-player')) {
            onMessage('This screen already has a Reel Player widget', 'error');
            return;
        }
        if (type === 'on-demand-player' && widgets.some((w) => w.type === 'on-demand-player')) {
            onMessage('This screen already has an On Demand Player widget', 'error');
            return;
        }
        const widget = createWidget(type, widgets);
        // On Demand Player is the bottom layer. The Reel Player sits just above it.
        setWidgets((list) => {
            const rest = type === 'reel-player' || type === 'on-demand-player' ? list : [...list, widget];
            const withNew = type === 'reel-player' || type === 'on-demand-player' ? [widget, ...list] : rest;
            const odp = withNew.find((item) => item.type === 'on-demand-player');
            return odp ? [odp, ...withNew.filter((item) => item !== odp)] : withNew;
        });
        setSelectedId(widget.id);
        setDirty(true);
        if (type !== 'reel-player') setSettingsId(widget.id);
    };

    const handleMove = (id: string, direction: 'up' | 'down' | 'top' | 'bottom') => {
        setWidgets((list) => {
            const idx = list.findIndex((w) => w.id === id);
            if (idx < 0) return list;
            const next = [...list];
            const [w] = next.splice(idx, 1);
            const target = direction === 'top' ? next.length : direction === 'bottom' ? 0 : direction === 'up' ? Math.min(next.length, idx + 1) : Math.max(0, idx - 1);
            next.splice(target, 0, w);
            const odp = next.find((item) => item.type === 'on-demand-player');
            return odp ? [odp, ...next.filter((item) => item !== odp)] : next;
        });
        setDirty(true);
    };

    const handleDelete = (id: string) => {
        setWidgets((list) => list.filter((w) => w.id !== id));
        setSelectedId((s) => (s === id ? null : s));
        setDirty(true);
    };

    const toggle = (id: string, key: 'visible' | 'locked') => {
        setWidgets((list) => list.map((w) => (w.id === id ? ({ ...w, [key]: !w[key] } as ScreenWidget) : w)));
        setDirty(true);
    };

    const handleSave = useCallback(async () => {
        if (saving) return;
        setSaving(true);
        try {
            const updated = await screensApi.saveComposer(screen._id, widgets);
            setWidgets(updated.composer?.widgets ?? widgets);
            setRevision(updated.composer?.revision ?? revision + 1);
            setDirty(false);
            onSaved(updated);
            onMessage(`Layout saved. The screen shows it after its next poll.`);
        } catch (error) {
            onMessage(`Could not save the layout: ${error instanceof Error ? error.message : String(error)}`, 'error');
        } finally {
            setSaving(false);
        }
    }, [saving, screen._id, widgets, revision, onSaved, onMessage]);

    const handleRevert = () => {
        if (!window.confirm('Discard the unsaved layout changes?')) return;
        setWidgets(screen.composer?.widgets ?? []);
        setRevision(screen.composer?.revision ?? 0);
        setSelectedId(null);
        setDirty(false);
    };

    // Ctrl/Cmd+S saves, Delete removes the selection, Escape deselects.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (viewOnly) return;
            const target = e.target as HTMLElement | null;
            const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                if (dirty) void handleSave();
                return;
            }
            if (typing || settingsId) return;
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
                const w = widgets.find((x) => x.id === selectedId);
                if (w && !w.locked) {
                    e.preventDefault();
                    handleDelete(selectedId);
                }
            } else if (e.key === 'Escape') {
                setSelectedId(null);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirty, selectedId, widgets, settingsId, handleSave, viewOnly]);

    useEffect(() => {
        if (!dirty) return undefined;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [dirty]);

    const hasReelPlayer = widgets.some((w) => w.type === 'reel-player');
    const settingsWidget = settingsId ? widgets.find((w) => w.id === settingsId) ?? null : null;
    const full = widgets.length >= COMPOSER_MAX_WIDGETS;

    const renderWidget = (w: ScreenWidget) => {
        switch (w.type) {
            case 'reel-player':
                return <ReelPlayerWidgetView config={w.config} label={screen.playlistName ? `Playlist: ${screen.playlistName}` : 'No playlist assigned yet'} />;
            case 'weather':
                return <WeatherWidgetView config={w.config} weather={weather[w.id] ?? null} />;
            case 'news-ticker':
                return (
                    <NewsTickerView
                        config={w.config}
                        items={newsItemsFrom(news[w.config.feedUrl] || [], w.config.fields, w.config.fieldJoiner, w.config.maxItems)}
                        placeholder={w.config.feedUrl ? 'Loading the news feed…' : 'Open settings to choose an RSS feed'}
                    />
                );
            case 'stock-ticker':
                return (
                    <StockTickerView
                        config={w.config}
                        quotes={stocks[w.id] || []}
                        placeholder={stocks[w.id] ? 'No prices for these symbols' : 'Loading stock prices…'}
                    />
                );
            case 'cricket-score':
                return <CricketScoreView config={w.config} payload={cricket[w.id]} placeholder timezone={screen.timezone} />;
            case 'clock':
                return <ClockWidgetView config={w.config} timezone={screen.timezone} />;
            case 'on-demand-player':
                return (
                    <OnDemandPlayerView
                        config={w.config}
                        qrUrl={screen.onDemandUrl}
                        queueCount={2}
                        next={{ id: 'preview', name: 'Next playlist', userFirstName: 'Sharad', state: 'downloading', progress: { bytesDone: 42, bytesTotal: 100, filesDone: 1, filesTotal: 2 } }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Box>
            {viewOnly && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                    You can only view the layout. For editing, please use a desktop machine.
                </Alert>
            )}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
                    {viewOnly
                        ? `${widgets.length} widget${widgets.length === 1 ? '' : 's'}${screen.timezone ? ` · Screen timezone ${screen.timezone}` : ''}`
                        : widgets.length === 0
                            ? 'No widgets: the screen plays its playlist full screen. Add widgets from the SideKick.'
                            : `${widgets.length} widget${widgets.length === 1 ? '' : 's'}${hasReelPlayer ? '' : ' · no Reel Player, so the playlist is not shown'}${screen.timezone ? ` · Screen timezone ${screen.timezone}` : ''}`}
                </Typography>
                {revision > 0 && <Chip size="small" variant="outlined" label={`Layout revision ${revision}`} />}
                {!viewOnly && dirty && <Chip size="small" variant="outlined" color="warning" label="Unsaved changes" />}
                {!viewOnly && (
                    <Button variant="outlined" color="secondary" size="small" startIcon={<UndoIcon />} disabled={!dirty || saving} onClick={handleRevert}>
                        Discard
                    </Button>
                )}
                {!viewOnly && (
                    <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                        disabled={!dirty || saving}
                        onClick={() => void handleSave()}
                    >
                        Save layout
                    </Button>
                )}
            </Stack>

            <Paper
                ref={stageRef}
                variant="outlined"
                onPointerDown={() => setSelectedId(null)}
                sx={{
                    height: { xs: 420, md: 'calc(100vh - 330px)' },
                    minHeight: 420,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.04)',
                    backgroundImage:
                        theme.palette.mode === 'dark'
                            ? 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)'
                            : 'radial-gradient(rgba(0,0,0,0.10) 1px, transparent 1px)',
                    backgroundSize: '16px 16px',
                }}
            >
                {!viewOnly && <FloatingSideKick
                    boundsRef={stageRef}
                    storageKey="screen-composer-sidekick-expanded"
                    expandedWidth={232}
                    groups={[
                        [
                            {
                                title: 'Add Reel Player Widget',
                                icon: <SmartDisplayIcon fontSize="small" />,
                                onClick: () => handleAdd('reel-player'),
                                disabled: hasReelPlayer || full,
                            },
                            { title: 'Add Weather Widget', icon: <WbSunnyIcon fontSize="small" />, onClick: () => handleAdd('weather'), disabled: full },
                            { title: 'Add News Ticker Widget', icon: <NewspaperIcon fontSize="small" />, onClick: () => handleAdd('news-ticker'), disabled: full },
                            { title: 'Add Stock Ticker Widget', icon: <ShowChartIcon fontSize="small" />, onClick: () => handleAdd('stock-ticker'), disabled: full },
                            { title: 'Add Cricket Score Widget', icon: <SportsCricketIcon fontSize="small" />, onClick: () => handleAdd('cricket-score'), disabled: full },
                            { title: 'Add Time Widget', icon: <ScheduleIcon fontSize="small" />, onClick: () => handleAdd('clock'), disabled: full },
                            { title: 'Add On Demand Player', icon: <OndemandVideoIcon fontSize="small" />, onClick: () => handleAdd('on-demand-player'), disabled: full || widgets.some((w) => w.type === 'on-demand-player') },
                        ],
                        [{ title: layersOpen ? 'Hide layers' : 'Show layers', icon: <LayersIcon fontSize="small" />, onClick: () => setLayersOpen((v) => !v), active: layersOpen }],
                    ]}
                />}

                {!viewOnly && layersOpen && (
                    <LayersPanel<ScreenWidget>
                        elements={widgets}
                        selectedId={selectedId}
                        onSelect={setSelectedId}
                        onMove={handleMove}
                        onToggleVisible={(id) => toggle(id, 'visible')}
                        onToggleLocked={(id) => toggle(id, 'locked')}
                        onDelete={handleDelete}
                        onClose={() => setLayersOpen(false)}
                        renderIcon={(w) => TYPE_ICON[w.type]}
                        renderSubtitle={(w, index) => `${WIDGET_LABELS[w.type]} · z${index + 1} · ${pct(w.width)} × ${pct(w.height)}`}
                        emptyText="No widgets yet. Use the SideKick to add a Reel Player, weather, a news ticker or the time."
                    />
                )}

                <Box
                    sx={{
                        width: design.width * scale,
                        height: design.height * scale,
                        position: 'relative',
                        flex: '0 0 auto',
                        boxShadow: `0 8px 32px ${alpha('#000', theme.palette.mode === 'dark' ? 0.6 : 0.25)}`,
                    }}
                >
                    <Box
                        aria-label={`${screen.name} layout ${design.width} by ${design.height} pixels`}
                        onPointerDown={(e) => {
                            e.stopPropagation();
                            setSelectedId(null);
                        }}
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: design.width,
                            height: design.height,
                            transform: `scale(${scale})`,
                            transformOrigin: 'top left',
                            overflow: 'hidden',
                            backgroundColor: '#000',
                        }}
                    >
                        {widgets.length === 0 && (
                            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.45)', fontSize: 48, textAlign: 'center', px: 8 }}>
                                Full-screen playlist
                            </Box>
                        )}
                        {widgets.map((w, index) => (
                            <ComposerWidgetFrame
                                key={w.id}
                                widget={w}
                                designWidth={design.width}
                                designHeight={design.height}
                                scale={scale}
                                zIndex={index + 1}
                                selected={!viewOnly && w.id === selectedId}
                                readOnly={viewOnly}
                                onSelect={setSelectedId}
                                onChange={(next, commit) => replaceWidget(next, commit)}
                                onOpen={(id) => {
                                    setSelectedId(id);
                                    setSettingsId(id);
                                }}
                            >
                                {renderWidget(w)}
                            </ComposerWidgetFrame>
                        ))}
                    </Box>
                </Box>

                <Chip
                    size="small"
                    label={`${design.width} × ${design.height} px · ${Math.round(scale * 100)}%`}
                    sx={{ position: 'absolute', right: 12, bottom: 12, fontWeight: 600, zIndex: 20, backgroundColor: alpha(theme.palette.background.paper, 0.9) }}
                />
            </Paper>

            <WidgetSettingsDialog
                open={Boolean(settingsWidget)}
                widget={settingsWidget}
                screenId={screen._id}
                screenLocation={screen.location || ''}
                screenTimezone={screen.timezone || 'UTC'}
                qrUrl={screen.onDemandUrl}
                onClose={() => setSettingsId(null)}
                onApply={(next) => {
                    replaceWidget(next);
                    setSettingsId(null);
                }}
            />
        </Box>
    );
};

export default ScreenComposer;
